var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import React, { forwardRef, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react-lite';
import { IMask } from 'react-imask';
import classNames from 'classnames';
import moment from 'moment-timezone';
import Icon from 'ziphy-web-shared/basic/assets/images/Icon';
import { Input } from 'ziphy-web-shared/basic/lib/forms/fields';
import { DatePickerModal } from 'ziphy-web-shared/basic/lib/functional';
import { $modal } from 'ziphy-web-shared/basic/lib/utilities/modals';
import { $time } from 'ziphy-web-shared/basic/utils/time';
import styles from './DateInput.module.scss';
const DateInput = forwardRef((_a, ref) => {
    var { field, onClick, clear, readonly, innerClassName, 
    //
    minDate: _minDate = -36500, // -100 years
    maxDate: _maxDate = 36500, // 100 years
    type = 'selectDay', timezone = $time.timezone, scrollTo, visibleFormat = 'MM/DD/YYYY', placeholder = visibleFormat } = _a, inputProps = __rest(_a, ["field", "onClick", "clear", "readonly", "innerClassName", "minDate", "maxDate", "type", "timezone", "scrollTo", "visibleFormat", "placeholder"]);
    const innerValue = useMemo(() => {
        if (!field.value)
            return '';
        const date = moment(field.value);
        return type === 'selectDateTime'
            ? date.tz(timezone).format(visibleFormat)
            : date.format(visibleFormat);
    }, [field.value, timezone, type, visibleFormat]);
    const parseDate = (date) => {
        if (date === 'today')
            return moment();
        if (typeof date === 'number') {
            return date >= 0 ? moment().add(date, 'days') : moment().subtract(Math.abs(date), 'days');
        }
        return moment(date);
    };
    const minDate = useMemo(() => parseDate(_minDate), [_minDate]);
    const maxDate = useMemo(() => parseDate(_maxDate), [_maxDate]);
    const handleOnChange = useCallback((value) => {
        if (moment(value, ['YYYY-MM-DD', visibleFormat, 'YYYY-MM-DDTHH:mm:ss.sssZ'], true).isValid()) {
            if (type === 'selectDateTime') {
                field.onChange(value);
            }
            else {
                field.onChange(moment(value, ['YYYY-MM-DD', visibleFormat]).format('YYYY-MM-DD'));
            }
        }
        else if (value === '') {
            field.onChange('');
        }
    }, [field, type, visibleFormat]);
    const getSelectedDate = useCallback(() => {
        const currentDate = moment().format();
        if (innerValue)
            return innerValue;
        return maxDate.isAfter(currentDate) ? currentDate : maxDate;
    }, [innerValue, maxDate]);
    const openModal = useCallback((event) => {
        event === null || event === void 0 ? void 0 : event.stopPropagation();
        if (inputProps.disabled)
            return;
        $modal.add(DatePickerModal, {
            selectedDate: getSelectedDate(),
            onChange: handleOnChange,
            minDate: minDate,
            maxDate: maxDate,
            type,
            scrollTo,
        });
    }, [inputProps.disabled, getSelectedDate, handleOnChange, minDate, maxDate, type, scrollTo]);
    const maskOptions = useMemo(() => {
        if (readonly)
            return undefined;
        const yearFrom = Number(minDate.format('YYYY'));
        const yearTo = Number(maxDate.format('YYYY'));
        const sameYear = yearFrom === yearTo;
        const monthFrom = Number(minDate.format('MM'));
        const monthTo = Number(maxDate.format('MM'));
        const sameMonth = monthFrom === monthTo;
        return {
            mask: Date,
            pattern: visibleFormat,
            lazy: true,
            autofix: true,
            min: minDate.clone().startOf('day').toDate(),
            max: maxDate.clone().endOf('day').toDate(),
            format(date) {
                return moment(date).format(visibleFormat);
            },
            parse(str) {
                const date = moment(str);
                if (date < minDate) {
                    handleOnChange(minDate.format(visibleFormat));
                    return minDate.toDate();
                }
                else if (date > maxDate) {
                    handleOnChange(maxDate.format(visibleFormat));
                    return maxDate.toDate();
                }
                return moment(str, visibleFormat).toDate();
            },
            blocks: {
                YYYY: sameYear
                    ? {
                        mask: '0000',
                        prepare(str, masked) {
                            return String(yearFrom)[masked.value.length] || '';
                        },
                        maxLength: 4,
                    }
                    : {
                        mask: IMask.MaskedRange,
                        from: yearFrom,
                        to: yearTo,
                        maxLength: 4,
                    },
                MM: {
                    mask: IMask.MaskedRange,
                    from: sameYear ? monthFrom : 1,
                    to: sameYear ? monthTo : 12,
                    maxLength: 2,
                },
                DD: {
                    mask: IMask.MaskedRange,
                    from: sameMonth && sameYear ? Number(minDate.format('DD')) : 1,
                    to: sameMonth && sameYear ? Number(maxDate.format('DD')) : 31,
                    maxLength: 2,
                },
            },
        };
    }, [handleOnChange, readonly, visibleFormat, minDate, maxDate]);
    return (_jsx(Input, Object.assign({ ref: ref, field: Object.assign(Object.assign({}, field), { value: innerValue, onChange: handleOnChange }), placeholder: placeholder, rightElement: _jsx(Icon, { type: "calendar", mode: inputProps.disabled ? undefined : 'withHover', onClick: openModal }), onClick: (event) => {
            if (readonly)
                openModal(event);
            onClick === null || onClick === void 0 ? void 0 : onClick(event);
        }, mask: maskOptions, clear: inputProps.disabled ? undefined : clear === true ? 'absolute' : clear, innerClassName: classNames(innerClassName, readonly && styles.readonly) }, inputProps)));
});
export default observer(DateInput);
